import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import { Toast, Field, PageHeader, ProductSearch } from './v2-utils';
import '../styles/v2.css';

function today() { return new Date().toISOString().split('T')[0]; }

function formatProductDisplay(p) {
  let s = p.product_name;
  if (p.variety) s += ` - ${p.variety}`;
  if (p.brand)   s += ` (${p.brand})`;
  if (p.size)    s += ` [${p.size}]`;
  return s;
}

function parseProductDetails(display) {
  let name, variety = '', brand = '', size = '';
  let rest = display;
  if (rest.includes('[') && rest.includes(']')) {
    const m = rest.match(/\[([^\]]+)\]$/);
    if (m) { size = m[1]; rest = rest.replace(/\s*\[[^\]]+\]$/, ''); }
  }
  if (rest.includes('(') && rest.includes(')')) {
    const m = rest.match(/\(([^)]+)\)$/);
    if (m) { brand = m[1]; rest = rest.replace(/\s*\([^)]+\)$/, ''); }
  }
  if (rest.includes(' - ')) { [name, variety] = rest.split(' - '); }
  else { name = rest; }
  return { name, variety, brand, size };
}

export default function AddPurchaseV2() {
  const navigate = useNavigate();
  const [productName, setProductName]       = useState('');
  const [productDetails, setProductDetails] = useState('');
  const [currentProduct, setCurrentProduct] = useState(null);
  const [isProductSelected, setIsProductSelected] = useState(false);
  const [unitTypes, setUnitTypes]           = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [orderPrice, setOrderPrice]         = useState('');
  const [quantity, setQuantity]             = useState('');
  const [discount, setDiscount]             = useState('0');
  const [purchaseDate, setPurchaseDate]     = useState(today());
  const [dateWarning, setDateWarning]       = useState('');
  const [searchResults, setSearchResults]   = useState([]);
  const [isOnline, setIsOnline]             = useState(navigator.onLine);
  const [suppliers, setSuppliers]           = useState([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [toast, setToast]                   = useState(null);
  const [loading, setLoading]               = useState(false);
  const justSelectedRef = useRef(false);
  const blurTimeoutRef  = useRef(null);

  useEffect(() => {
    const up = () => setIsOnline(true), down = () => setIsOnline(false);
    window.addEventListener('online', up); window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current); };
  }, []);

  useEffect(() => {
    if (productName.length > 2 && isOnline && !justSelectedRef.current) {
      axiosInstance.get(`/products/search?q=${productName}`).then(res => {
        const unique = Object.values(res.data.reduce((acc, p) => {
          const k = `${p.product_name}-${p.variety||''}-${p.brand||''}-${p.size||''}`;
          if (!acc[k]) acc[k] = p; return acc;
        }, {}));
        setSearchResults(unique);
      }).catch(() => setSearchResults([]));
    } else { setSearchResults([]); }
    if (justSelectedRef.current) justSelectedRef.current = false;
  }, [productName, isOnline]);

  const selectedUnit = unitTypes.find(u => String(u.unit_id) === String(selectedUnitId));
  const total = Math.max(0, (parseFloat(orderPrice)||0) * (parseFloat(quantity)||0) - (parseFloat(discount)||0)).toFixed(2);

  const handleSelect = (p) => {
    if (blurTimeoutRef.current) { clearTimeout(blurTimeoutRef.current); blurTimeoutRef.current = null; }
    justSelectedRef.current = true;
    const display = formatProductDisplay(p);
    setProductName(display); setProductDetails(display);
    setCurrentProduct(p); setIsProductSelected(true);
    setSelectedUnitId(''); setOrderPrice(''); setSearchResults([]);
    setSuppliers([]); setSelectedSupplierId('');
    axiosInstance.get(`/units/product/${p.product_id}`).then(res => {
      setUnitTypes(res.data.filter(u => u.unit_category === 'buying').map(u => ({ unit_type: u.unit_type, unit_id: u.unit_id, unit_category: u.unit_category })));
    }).catch(() => {});
  };

  const handleUnitChange = async (unitId) => {
    setSelectedUnitId(unitId);
    setSuppliers([]); setSelectedSupplierId('');
    if (!unitId || !currentProduct) return;
    try {
      const [priceRes, suppliersRes] = await Promise.all([
        axiosInstance.get(`/purchases/price-suggestions/${currentProduct.product_id}/${unitId}`),
        axiosInstance.get(`/units/${unitId}/suppliers`),
      ]);
      if (priceRes.data.has_price_history) setOrderPrice(priceRes.data.suggested_order_price.toString());
      else setOrderPrice('');
      setSuppliers(suppliersRes.data);
    } catch { setOrderPrice(''); }
  };

  const handleProductChange = (val) => {
    setProductName(val); setProductDetails(''); setIsProductSelected(false);
    setCurrentProduct(null); setSelectedUnitId(''); setOrderPrice(''); setUnitTypes([]);
    setSuppliers([]); setSelectedSupplierId('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUnit) { setToast({ message: 'Please select a unit type.', severity: 'error' }); return; }
    if (suppliers.length > 0 && !selectedSupplierId) { setToast({ message: 'Please select a supplier.', severity: 'error' }); return; }
    const { name, variety, brand, size } = parseProductDetails(productDetails);
    const chosenSupplier = suppliers.find(s => String(s.supplier_id) === String(selectedSupplierId));
    setLoading(true);
    try {
      await axiosInstance.post('/purchases', {
        product_name: name, variety: variety||'', brand: brand||'', size: size||'',
        supplier_name: chosenSupplier?.name || null,
        supplier_id: chosenSupplier?.supplier_id || null,
        market_name: null,
        order_price: orderPrice, quantity, purchase_date: purchaseDate,
        unit_id: selectedUnitId, unit_category: selectedUnit.unit_category,
        discount,
        trans_id: `TXN-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
      });
      setToast({ message: 'Purchase recorded successfully!', severity: 'success' });
      setProductName(''); setProductDetails(''); setCurrentProduct(null);
      setIsProductSelected(false); setUnitTypes([]); setSelectedUnitId('');
      setOrderPrice(''); setQuantity(''); setDiscount('0'); setPurchaseDate(today()); setDateWarning('');
      setSuppliers([]); setSelectedSupplierId('');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || '';
      setToast({ message: msg || 'Failed to record purchase. Check inventory exists.', severity: 'error' });
    } finally { setLoading(false); }
  };

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <PageHeader title="Add Purchase" subtitle="Record a new stock purchase" onBack={() => navigate('/new/dashboard/purchases/view')} />

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          <div className="v2-card" style={{ padding: 24 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16, margin: '0 0 16px' }}>Product</p>
            <Field label="Search Product" hint="Type at least 3 characters to search">
              <ProductSearch value={productName} onChange={handleProductChange} onSelect={handleSelect}
                onBlur={() => { blurTimeoutRef.current = setTimeout(() => setSearchResults([]), 150); }}
                onFocus={() => { if (blurTimeoutRef.current) { clearTimeout(blurTimeoutRef.current); blurTimeoutRef.current = null; } }}
                results={searchResults} placeholder="Search by product name…" />
            </Field>
          </div>

          {isProductSelected && (
            <div className="v2-card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Purchase Details</p>

              <Field label="Buying Unit">
                <div style={{ position: 'relative' }}>
                  <select className="v2-input" style={{ paddingRight: 36, appearance: 'none', cursor: 'pointer' }}
                    value={selectedUnitId} onChange={e => handleUnitChange(e.target.value)} required>
                    <option value="">Select buying unit…</option>
                    {unitTypes.map(u => <option key={u.unit_id} value={u.unit_id}>{u.unit_type}</option>)}
                  </select>
                  <svg style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                </div>
                {unitTypes.length === 0 && <p style={{ fontSize: 11, color: '#f59e0b', margin: '4px 0 0', fontWeight: 500 }}>No buying units found for this product.</p>}
              </Field>

              <Field label="Supplier">
                <div style={{ position: 'relative' }}>
                  <select className="v2-input"
                    style={{ paddingRight: 36, appearance: 'none', cursor: (!selectedUnitId || suppliers.length === 0) ? 'not-allowed' : 'pointer', opacity: (!selectedUnitId || suppliers.length === 0) ? 0.5 : 1 }}
                    value={selectedSupplierId} onChange={e => setSelectedSupplierId(e.target.value)}
                    required={suppliers.length > 0} disabled={!selectedUnitId || suppliers.length === 0}>
                    <option value="">
                      {!selectedUnitId ? 'Select a unit first' : suppliers.length === 0 ? 'No suppliers available' : 'Select supplier…'}
                    </option>
                    {suppliers.map(s => <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>)}
                  </select>
                  <svg style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                </div>
              </Field>

              <div className="v2-sale-price-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label={selectedUnit ? `Order Price per ${selectedUnit.unit_type} (K)` : 'Order Price (K)'}>
                  <input className="v2-input" type="number" placeholder="0.00" value={orderPrice} onChange={e => setOrderPrice(e.target.value)} min="0" step="0.01" required />
                </Field>
                <Field label="Quantity">
                  <input className="v2-input" type="number" placeholder="0" value={quantity} onChange={e => setQuantity(e.target.value)} min="0" step="0.01" required />
                </Field>
              </div>

              <Field label="Discount (K)" hint="Optional — enter 0 for no discount">
                <input className="v2-input" type="number" placeholder="0.00" value={discount} onChange={e => setDiscount(Math.max(0, e.target.value||0))} min="0" step="0.01" />
              </Field>

              <Field label="Purchase Date">
                <input className="v2-input" type="date" value={purchaseDate} onChange={e => { setPurchaseDate(e.target.value); setDateWarning(e.target.value > today() ? 'Note: Future date entered.' : ''); }} required />
                {dateWarning && <p style={{ fontSize: 11, color: '#f59e0b', margin: '4px 0 0', fontWeight: 500 }}>{dateWarning}</p>}
              </Field>
            </div>
          )}

          {isProductSelected && selectedUnitId && orderPrice && quantity && (
            <div style={{ background: 'linear-gradient(135deg,#f0fdf4,#ecfdf5)', borderRadius: 16, padding: 20, border: '1.5px solid #a7f3d0' }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Order Summary</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, color: '#475569' }}>{quantity} × K {parseFloat(orderPrice||0).toFixed(2)}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>K {(parseFloat(orderPrice||0)*parseFloat(quantity||0)).toFixed(2)}</span>
                </div>
                {parseFloat(discount)>0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: 13, color: '#475569' }}>Discount</span><span style={{ fontSize: 13, fontWeight: 600, color: '#ef4444' }}>− K {parseFloat(discount).toFixed(2)}</span></div>}
                <div style={{ height: 1, background: '#a7f3d0', margin: '4px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#065f46' }}>Total</span>
                  <span style={{ fontSize: 18, fontWeight: 900, color: '#065f46', letterSpacing: '-0.02em' }}>K {total}</span>
                </div>
              </div>
            </div>
          )}

          {isProductSelected && (
            <button type="submit" className="v2-btn-submit" disabled={loading} style={{ fontSize: 15 }}>
              {loading ? 'Saving…' : 'Add Purchase'}
            </button>
          )}
        </div>
      </form>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
